import {
    addDays,
    addMinutes,
    addSeconds,
    DateArg,
    format,
    formatISO,
    isAfter,
    isBefore,
    isDate,
    startOfDay
} from "date-fns";
import {isString, trim} from "lodash";
import {z} from "zod/v4";

export const Flexidate = z.union([
    z.string(),
    z.number(),
    z.date(),
]);

export function stringify(flexidate: DateArg<Date>) {
    return formatISO(flexidate);
}

const TSTZ_NEGATIVE_INFINITY_STR = "-infinity";
const TSTZ_INFINITY_STR = "infinity";
export const TSTZ_NEGATIVE_INFINITY = -8640000000000000;
export const TSTZ_INFINITY = 8640000000000000;

// export function isDateFinite(d: Date | undefined | null): boolean {
//     return d !== undefined && d !== null && d !== TSTZ_NEGATIVE_INFINITY && d !== TSTZ_INFINITY;
// }

export function isTstzFinite(maybe: DateArg<Date> | undefined | null): boolean {
    if (maybe === undefined || maybe === null || maybe === ""
        || maybe === TSTZ_INFINITY_STR || maybe === TSTZ_NEGATIVE_INFINITY_STR
        || maybe === TSTZ_INFINITY || maybe === TSTZ_NEGATIVE_INFINITY) {
        return false;
    }
    if (isDate(maybe)) {
        const mv = maybe.valueOf();
        if (mv === TSTZ_INFINITY || mv === TSTZ_NEGATIVE_INFINITY) {
            return false;
        }
    }
    return true;
}

export function stringToTstz(s: string): DateArg<Date> {
    s = trim(s, "\"");
    if (s === TSTZ_NEGATIVE_INFINITY_STR) {
        return TSTZ_NEGATIVE_INFINITY;
    }
    if (s === TSTZ_INFINITY_STR) {
        return TSTZ_INFINITY;
    }
    return s;
}

const wpcWireTstzSpanRegex =
    /(?<lbnd>[([])(?<lower>[^,)\]]*),(?<upper>[^)\]]*)(?<ubnd>\)|\])/;

export const TstzLowerBound = z.literal(["[", "("]);
export type TstzLowerBound = z.infer<typeof TstzLowerBound>;
export const TstzUpperBound = z.literal([")", "]"]);
export type TstzUpperBound = z.infer<typeof TstzUpperBound>;

export const Valid = z.object({
    valid: z.literal(true),
});

export const TstzSpan = z.object({
    valid: z.boolean(),
    lowerBound: TstzLowerBound,
    lower: z.nullable(Flexidate),
    upper: z.nullable(Flexidate),
    upperBound: TstzUpperBound,
});

export type TstzSpan = z.infer<typeof TstzSpan> & {
    toString: () => string,
    contains: (maybe: DateArg<Date> | null | undefined) => boolean,
    containsNow: () => boolean,
    overlaps: (maybe: TstzSpan) => boolean,
    guaranteedIncluded: () => DateArg<Date> | undefined,
    isDegenerate: () => boolean,
    asTimeIntervalString: () => string | undefined,
}

const zeroTstzSpan: z.infer<typeof TstzSpan> = {
    valid: false,
    lowerBound: "[",
    lower: null,
    upper: null,
    upperBound: ")",
};

export const invalidTstzSpan: TstzSpan = {
    ...zeroTstzSpan,

    toString: () => "",
    contains: () => false,
    containsNow: () => false,
    overlaps: () => false,
    guaranteedIncluded: () => undefined,
    isDegenerate: () => true,
    asTimeIntervalString: () => "",
};

export function spawnTstzSpan(arg: string | Partial<TstzSpan> | undefined | null): TstzSpan {
    if (!arg) {
        return invalidTstzSpan;
    }

    const rvgu: z.infer<typeof TstzSpan> = {...zeroTstzSpan};

    if (isString(arg)) {
        const rematch = wpcWireTstzSpanRegex.exec(arg);
        if (!rematch?.groups) {
            return invalidTstzSpan;
        }
        if (rematch.groups.lower) {
            rvgu.lower = stringToTstz(rematch.groups.lower);
        }
        if (rematch.groups.upper) {
            rvgu.upper = stringToTstz(rematch.groups.upper);
        }
        if (rvgu.lower === TSTZ_INFINITY || rvgu.lower === TSTZ_INFINITY_STR) {
            return invalidTstzSpan;
        }
        if (rvgu.lower === TSTZ_NEGATIVE_INFINITY || rvgu.lower === TSTZ_NEGATIVE_INFINITY_STR) {
            rvgu.lower = null;
        } else {
            rvgu.lowerBound = rematch.groups.lbnd as TstzLowerBound;
        }
        if (rvgu.upper === TSTZ_NEGATIVE_INFINITY) {
            return invalidTstzSpan;
        }
        if (rvgu.upper === TSTZ_INFINITY) {
            rvgu.upper = null;
        } else {
            rvgu.upperBound = rematch.groups.ubnd as TstzUpperBound;
        }
    } else {
        rvgu.lowerBound = arg.lowerBound ?? "[";
        rvgu.lower = arg.lower ?? null;
        rvgu.upper = arg.upper ?? null;
        rvgu.upperBound = arg.upperBound ?? ")";
    }

    rvgu.valid = (rvgu.lower === null && rvgu.upper !== null) ||
        (rvgu.upper === null && rvgu.lower !== null) ||
        (!!rvgu.upper && !!rvgu.lower && rvgu.upper >= rvgu.lower);

    const rv: TstzSpan = {
        ...rvgu,
        toString: function () {
            if (!this.valid) {
                return "";
            }
            const snippets: string[] = [this.lowerBound];
            if (this.lower) {
                snippets.push(formatISO(this.lower));
            }
            snippets.push(",");
            if (this.upper) {
                snippets.push(formatISO(this.upper));
            }
            snippets.push(this.upperBound);
            const rv = snippets.join("");
            return rv;
        },

        contains: function (maybe: DateArg<Date> | null | undefined) {
            if (!maybe) {
                return false;
            }
            if (!this.valid) {
                return false;
            }
            if (this.lower && ((this.lowerBound === "[" && isBefore(maybe, this.lower)) ||
                (this.lowerBound === "(" && !isAfter(maybe, this.lower)))) {
                return false;
            }
            if (this.upper && ((this.upperBound === "]" && isAfter(maybe, this.upper)) ||
                (this.upperBound === ")" && !isBefore(maybe, this.upper)))) {
                return false;
            }
            return true;
        },

        containsNow: function () {
            return this.contains(new Date());
        },

        overlaps: function (maybe: TstzSpan) {
            // TODO proper handling of unbounded cases
            return this.contains(maybe.lower) || this.contains(maybe.upper);
        },

        isDegenerate: function () {
            return !!this.lower && !!this.upper && !isAfter(this.upper, this.lower);
        },

        // guaranteed to be contained. this is harder than it looks, because of
        // rounding problems when round-tripping to JSON.
        guaranteedIncluded: function () {
            if (!this.valid) {
                return undefined;
            }
            if (this.isDegenerate()) {
                return this.lower!;
            }
            const rv = addSeconds(this.lower!, 1);
            if (!!this.upper && !isAfter(this.upper, rv)) {
                throw new Error(`impossible to split time span ${this.toString()}`)
            }
            return rv;
        },

        asTimeIntervalString: function () {
            if (!this.valid || !this.lower || !this.upper) {
                return undefined;
            }
            // TODO how to handle unbounded ends
            const rv = `${format(this.lower, "HH:mm")}-${format(this.upper, "HH:mm")}`;
            return rv;
        }
    }

    return rv;
}

export const TSTZ_FOREVER = spawnTstzSpan({
    valid: true,
    lower: TSTZ_NEGATIVE_INFINITY,
    upper: TSTZ_INFINITY,
});

export const ValidTstzSpan
    = z.intersection(TstzSpan, Valid);
export type ValidTstzSpan = z.infer<typeof ValidTstzSpan>;

export function makeDayTstzSpan(when: DateArg<Date>) {
    const bod = startOfDay(when);
    const rv = spawnTstzSpan({
        lowerBound: "[",
        lower: bod,
        upper: addDays(bod, 1),
        upperBound: ")",
        valid: true,
    });
    return rv;
}

export interface AsofSearchParams {
    asof?: DateArg<Date>;
}

export function validateAsofSearchParams(raw: Record<string, unknown>): AsofSearchParams {
    if (isDate(raw.asof)) {
        return {asof: raw.asof};
    } else if (isString(raw.asof)) {
        const d = new Date(raw.asof);
        if (!isNaN(d.getTime())) {
            return {asof: d};
        }
    }
    return {};
}

export function nowSpan() {
    const now = new Date();
    return spawnTstzSpan({
        lower: now,
        upper: addMinutes(now, 1),
    });
}

export function todaySpan() {
    const dayStart = startOfDay(new Date());
    return spawnTstzSpan({lower: dayStart, upper: addDays(dayStart, 1)});
}


